// @flow
import React, { useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import i18n from "i18next";
import k from "src/i18n/keys";
import * as R from "ramda";

import {
  getAllParticipants,
  getChatroomNotificationFilter,
} from "src/reducers";
import Icon from "src/icons";
import * as colors from "src/styles/constants/colors";
import { MenuItem } from "src/components/Unifize/Menu/MenuItem";

import type { ChatroomNotificationFilter, UID } from "src/types";

import {
  MenuContainer,
  FilterMenuContent,
  ToggleIconContainer,
  MenuIconContainer,
} from "./styles";
import UserFilterMenu from "./UserFilterMenu";
import RecordUpdatesMenu from "./RecordUpdatesMenu";
import AutomatorsMenu from "./AutomatorsMenu";

const Filter = () => {
  const filterMenuRef = useRef(null);
  const menuContainerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<?number>(null);
  const dispatch = useDispatch();

  const chatroomParticipants: Array<UID> = useSelector(({ app }) =>
    R.uniq(getAllParticipants(app)),
  );
  const currentFilter: ChatroomNotificationFilter = useSelector(({ app }) =>
    getChatroomNotificationFilter(app),
  );
  const automatorUIDs = useMemo(
    () => Object.keys(currentFilter.automators),
    [currentFilter.automators],
  );
  const currentFilterUsers = useMemo(
    () => new Set(currentFilter.users),
    [currentFilter.users],
  );
  const currentFilterAutomatorUIDs = useMemo(
    () => new Set(automatorUIDs),
    [automatorUIDs],
  );

  const toggleMenu = (e: Event, index: number) => {
    setActiveSubmenu((prev) => (prev === index ? null : index));
  };

  return (
    <MenuContainer ref={menuContainerRef}>
      <MenuItem
        onClick={(e) => {
          // Prevents the more menu's VStack's onClick from being triggered (MoreMenu/index.js:109)
          e.stopPropagation();
          if (filterMenuRef.current) {
            filterMenuRef.current.blur();
          }
          setOpen(!open);
        }}
        tabIndex={0}
        onKeyPress={(e) => {
          e.stopPropagation();
          if (filterMenuRef.current) {
            filterMenuRef.current.blur();
          }
          setOpen(!open);
        }}
        padding="l"
        height="xxxl"
      >
        <MenuIconContainer>
          <Icon type="chatFilter" fill={colors.active} />
        </MenuIconContainer>
        <span>{i18n.t(k.FILTER)}</span>
        {open ? (
          <ToggleIconContainer>
            <Icon type="caretUp" />
          </ToggleIconContainer>
        ) : (
          <ToggleIconContainer>
            <Icon type="caretDown" />
          </ToggleIconContainer>
        )}
      </MenuItem>
      {open && (
        <FilterMenuContent ref={filterMenuRef}>
          {chatroomParticipants.length !== 0 && (
            <MenuItem
              onMouseEnter={() => setActiveSubmenu(0)}
              onMouseLeave={() => setActiveSubmenu(null)}
              tabIndex={0}
              onClick={(e: Event) => e.stopPropagation()}
              onKeyDown={(e) => toggleMenu(e, 0)}
              position="relative"
              height="xxxl"
              hoverColor="rgba(133, 133, 133, 0.05)"
              padding="0 1rem 0 2.5rem"
            >
              <MenuIconContainer>
                <Icon type="chatFilterUser" fill={colors.active} />
                {i18n.t(k.USER)}
              </MenuIconContainer>
              <Icon type="chevronRight" />
              {activeSubmenu === 0 && (
                <UserFilterMenu
                  dispatch={dispatch}
                  participants={chatroomParticipants}
                  filteredUsers={currentFilterUsers}
                  automatorUIDs={currentFilterAutomatorUIDs}
                  menuRef={menuContainerRef}
                />
              )}
            </MenuItem>
          )}
          <MenuItem
            onMouseEnter={() => setActiveSubmenu(1)}
            onMouseLeave={() => setActiveSubmenu(null)}
            tabIndex={0}
            onClick={(e: Event) => e.stopPropagation()}
            onKeyDown={(e) => toggleMenu(e, 1)}
            position="relative"
            height="xxxl"
            hoverColor="rgba(133, 133, 133, 0.05)"
            padding="0 1rem 0 2.5rem"
          >
            <MenuIconContainer>
              <Icon type="chatFilterRecordUpdates" fill={colors.active} />
              {i18n.t(k.CONVERSATIONS)}
            </MenuIconContainer>
            <Icon type="chevronRight" />
            {activeSubmenu === 1 && (
              <RecordUpdatesMenu
                dispatch={dispatch}
                filteredRecords={currentFilter.types}
                menuRef={menuContainerRef}
              />
            )}
          </MenuItem>
          {automatorUIDs.length !== 0 && (
            <MenuItem
              onMouseEnter={() => setActiveSubmenu(2)}
              onMouseLeave={() => setActiveSubmenu(null)}
              tabIndex={0}
              onClick={(e: Event) => e.stopPropagation()}
              onKeyDown={(e) => toggleMenu(e, 2)}
              position="relative"
              height="xxxl"
              hoverColor="rgba(133, 133, 133, 0.05)"
              padding="0 1rem 0 2.5rem"
            >
              <MenuIconContainer>
                <Icon type="chatFilterUser" fill={colors.active} />
                Automators
              </MenuIconContainer>
              <Icon type="chevronRight" />
              {activeSubmenu === 2 && (
                <AutomatorsMenu
                  dispatch={dispatch}
                  automatorUIDs={automatorUIDs}
                  automatorFilter={currentFilter.automators}
                  menuRef={menuContainerRef}
                />
              )}
            </MenuItem>
          )}
        </FilterMenuContent>
      )}
    </MenuContainer>
  );
};

export default Filter;
